import type { ApiResponse } from '@shared/types'
import type { RTrunk, RTrunks } from 'v2/api-console/applications/types'
import type { RSmsAlertSettings } from 'v2/api-console/messaging/delivery-alerts/types'
import type { RSmsOtherSettings } from 'v2/api-console/messaging/other-settings/types'
import type { LookupResponse } from 'v2/api-console/numbers/lookup/types'
import type { RNumbersOtherSettings } from 'v2/api-console/numbers/other-settings/types'
import type { PortInOrderDetails, RListPortInOrders } from 'v2/api-console/numbers/port-in/types'
import type { RListUnrentedNumbers } from 'v2/api-console/numbers/unrented/types'
import type {
  CreateEndpointPayload,
  REndpoint,
  REndpointRouting,
  RListEndpoints,
  UpdateEndpointPayload,
  UpdateEndpointRoutingPayload,
} from 'v2/api-console/voice/endpoints/types'
import type { RListRentalSummary } from 'v2/api-console/voice/rental-summary/types'
import type {
  RVoiceSettings,
  UpdateVoiceSettingPayload,
} from 'v2/api-console/voice/voice-settings/types'
import type {
  RZentrunkFileCaptureConsent,
  UpdateZentrunkFileCaptureConsentPayload,
} from 'v2/api-console/zentrunk/sip-trunking-settings/types'
import type { OnboardingFormData } from 'v2/pages/onboarding/types'
import type {
  RAIAssistant,
  RChatApp,
  RChatAppListItem,
  RChatAppPayload,
  RDemoQuestions,
} from 'v2/pages/settings/chat/types'
import type { InternalNumberField } from 'v2/pages/settings/forwarding-contacts/internal-number-sheet'
import type {
  ComplianceV2ApplicationResponse,
  ComplianceV2ApplicationsListResponse,
  ComplianceV2CreateResponse,
  ComplianceV2IndiaCafBusinessVerificationResponse,
  ComplianceV2IndiaCafResponse,
  ComplianceV2IndiaCafUpdateResponse,
  ComplianceV2RequirementsRequest,
  ComplianceV2RequirementsResponse,
} from 'v2/pages/settings/number-compliance-v2/types-v2'
import type { AgentStatusFormFields } from 'v2/pages/settings/org-status/sheet'
import type { ConfigureNumberForm } from 'v2/pages/settings/phone-numbers/configure-number.config'
import type {
  RSenderId,
  RSenderIdCountryOptions,
  RSenderIdListResponse,
  RSenderIdRegisterPayload,
} from 'v2/pages/settings/phone-numbers/sender-id-types'
import type {
  RAuthenticatorAppPayload,
  RAuthenticatorAppResponse,
  RAuthenticatorVerify,
  RAuthenticatorVerifyResponse,
  RRecoveryCodes,
  RSendOtpForPhoneNumberChangePayload,
  RSendOtpForPhoneNumberChangeResponse,
  RTwoFADetailsResponse,
  RTwoFaPhoneDetailsResponse,
  RVerifyOtpForPhoneNumberChangePayload,
  RVerifyOtpForPhoneNumberChangeResponse,
  RVerifyUserPayload,
  RVerifyUserResponse,
  RVerifyUserTwoFaOtpPayload,
} from 'v2/pages/settings/security/types'
import type { HolidayFormFields, RPermissions } from 'v2/pages/settings/types'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import { BackendServiceName } from 'v2/types'
import type { RAuthDetails, RLoginResponse } from 'v2/types/api/auth'
import type {
  CredentialPayload,
  InitiateCallerIdVerificationPayload,
  IPAccessControlList,
  IPAccessControlListPayload,
  OriginationURIPayload,
  RAccountConcurrency,
  RAgentNumberPayload,
  RAgentSettings,
  RAgentStatusUpdate,
  RAllowedBuyCountries,
  RBusinessHoursList,
  RBusinessHoursPayload,
  RChannelConfiguration,
  RConversationSettings,
  RCreateIPAccessMutation,
  RCredential,
  RCredentialMutation,
  RDestination,
  RDestinationsList,
  RDisposition,
  RDispositionList,
  RDuplicateFlow,
  RFlowOptionsPayload,
  RFlows,
  RHolidayCreateResponse,
  RHolidayDetails,
  RHolidayGroupsList,
  RInitiateCallerIdVerification,
  RInternalNumber,
  RIPAccessControlList,
  RKeyContacts,
  RKeyContactsPayload,
  RNonTeamMembersList,
  RNumberBulkDelete,
  RNumberCnamUpdate,
  RNumberSearch,
  ROrganizationLite,
  ROrgStatus,
  ROriginationURI,
  ROriginationURIInternal,
  ROriginationURIMutation,
  RPhoneNumber,
  RPhoneNumbersPaginated,
  RPlivoApplication,
  RPlivoApplications,
  RPlivoApplicationsInternal,
  RPlivoNumbers,
  RProfilePermissions,
  RPublicOriginationURI,
  RQueue,
  RRolesList,
  RRoutingSettings,
  RSandboxAddPayload,
  RSandboxAddResponse,
  RSandboxNumbers,
  RSandboxVerifyResponse,
  RSipCredentials,
  RSipTrunkPlatform,
  RSipTrunkPlatforms,
  RSkill,
  RSkillsList,
  RStatusConfigurations,
  RStatusList,
  RSubaccount,
  RSubaccounts,
  RSwitchOrganizationPayloadV2,
  RTeamCreatePayload,
  RTeamsList,
  RTeamUpdatePayload,
  RTimezonesList,
  RUsageLimit,
  RUserDelete,
  RUserProfile,
  RUserProfilePayload,
  RUsersList,
  RUserUpdate,
  RVerifiedCallerId,
  RVerifiedCallerIds,
  RVerifyCallerId,
  SandboxOtpChannel,
  UsageLimitPayload,
  VerifyCallerIdPayload,
} from 'v2/types/api/settings'
import { baseService } from './base-services'
import { createService, extractBlob } from './create-service'

// WhatsApp Business Account Onboarding types
interface WabaOnboardingRequest {
  code: string
}

interface WabaOnboardingResponse {
  message: string
  waba_id?: string
}

interface WabaUpdateResponse {
  message: string
  status: string
}

// WhatsApp response types
interface WhatsAppPreVerifiedResponse {
  pre_verified_id: string
  is_pre_verified: boolean
  display_name?: string
  category?: string
  session_uuid?: string
}

interface WhatsAppVerifyOtpResponse {
  success: boolean
  status?: string
}

// Plivo Verification types

/**
 * number_service sanitises E.164 itself, but a literal "+" inside a URL path is
 * decoded as a space by the time it reaches upstream, so send digits only.
 */
const sanitizeSandboxNumber = (number: string) => number.replace(/\D/g, '')

const SETTINGS_ENDPOINTS = {
  supportLogin: '/support/login',
  pylonLogin: '/pylon/login',
  listOrgMembers: (uuid?: string) => `/admin/org-members${uuid ? `/${uuid}` : '?paginated=false'}`,
  businessHours: (uuid?: string) => `/contacto-config/businesshours${uuid ? `/${uuid}` : ''}`,
  listHolidayGroups: (uuid?: string) => `/contacto-config/holidays/groups${uuid ? `/${uuid}` : ''}`,
  listHolidays: (uuid?: string) => `/contacto-config/holidays${uuid ? `/${uuid}` : ''}`,
  listTeams: (uuid?: string) => `/rbac/teams${uuid ? `/${uuid}` : '?paginated=false'}`,
  listRoles: '/rbac/roles',
  accountNumbers: (number?: string) => `/accountnumbers${number ? `/${number}` : ''}`,
  unlinkNumberFromApplication: (phoneNumber: string) =>
    `/accountnumbers/${phoneNumber}/unlink-application`,
  phoneNumbersPaginated: '/v3/contacto-core/accountnumbers',
  exportPhoneNumbers: '/v1/contacto-core/accountnumbers/export',
  listSkills: (uuid?: string) => `/orgs/skills${uuid ? `/${uuid}` : '?paginated=false'}`,
  validateSkills: (uuid: string) => `/orgs/skills/${uuid}/validate`,
  status: (skillID?: string) => `/orgs/status${skillID ? `/${skillID}` : ''}`,
  updateAgentStatus: `/agents/status`,
  statusConfigurations: '/orgs/event/config',
  markInactivity: '/accounts/inactivity',
  listDispositions: (type = true) => `/calls/dispositions?fetch_disabled=${type}`,
  reorderStaus: '/orgs/status/reorder',
  reorderDispositions: '/calls/dispositions/reorder',
  updateDisposition: (uuid?: string) => `calls/dispositions${uuid ? `/${uuid}` : ''}`,
  phoneNumbers: (param: string) => `/phonenumbers/${param}`,
  // listFlows: '/contacto-conversation-management/config/v1/phlos',
  listFlows: (type?: string) => `/contacto-config/phlo${type ? `?phlo_type=${type}` : ''}`,
  flowOptions: '/contacto-config/phlo/options',
  duplicateFlow: (id: string) => `/contacto-config/phlo/copy/${id}`,
  deleteFlow: (id: string) => `/contacto-config/phlo/${id}`,
  updateFlow: (id: string) => `/contacto-config/phlo/${id}`,
  publishFlow: (id: string) => `/contacto-config/phlo/${id}/publish_phlo`,
  pauseFlow: (id: string) => `/contacto-config/phlo/${id}/pause`,
  resumeFlow: (id: string) => `/contacto-config/phlo/${id}/resume`,
  listQueues: (uuid?: string) => `/contacto-config/queues${uuid ? `/${uuid}` : ''}`,
  detailedQueue: (uuid: string, state?: string) =>
    `/contacto-config/queue/${uuid}${state ? `/${state}` : ''}`,
  listNonTeamMembers: '/rbac/teams/non-members',
  listTimezones: '/contacto-config/businesshours/timezones',
  getProfilePermissions: '/rbac/update_profile_permission',
  routingSettings: '/routing_settings',
  settings: '/orgs/settings',
  channelConfiguration: (channel: string) => `/configuration/${channel}`,
  agentSettings: '/settings/aom',
  profile: '/accounts/profile',
  accountConcurrency: '/account-limits/concurrency',
  sipCredentials: '/accounts/sip-credentials',
  usageLimit: (accountId: string) => `/internal/account/id/${accountId}/usage-limit/`,
  listOrganizations: '/accounts/list-all-orgs',
  switchOrganizationV2: () => `/accounts/org-switch-login-v2`,
  updateName: '/accounts/update-name',
  keyContacts: '/accounts/key-contacts',
  updateOrgName: '/update-org-name',
  internalNumbers: (contact_id?: number) =>
    `/cdl/internal-contacts${contact_id ? `/${contact_id}` : ''}`,
  conversationsConfiguration: '/orgs/conversations/settings',
  // Plivo Verified Caller IDs via Hodor Proxy
  // Sandbox verified destinations. Proxied by contacto-core to number_service,
  // which holds the Basic Auth credential. auth_id is resolved server-side from
  // AOM_UUID, so it never appears in the path here.
  sandboxNumbers: '/number-service/sandbox_numbers',
  sandboxNumber: (number: string) =>
    `/number-service/sandbox_numbers/${sanitizeSandboxNumber(number)}`,
  sandboxNumberVerify: (number: string) =>
    `/number-service/sandbox_numbers/${sanitizeSandboxNumber(number)}/verify`,
  sandboxNumberResend: (number: string) =>
    `/number-service/sandbox_numbers/${sanitizeSandboxNumber(number)}/resend`,
  verifiedCallerIds: '/plivo-api/VerifiedCallerId/',
  // Port-in Management
  portInOrders: '/port-in/orders',
  portInOrderDetails: (requestId: string) => `/port-in/orders/${requestId}`,
  portInNumberConfigDetails: '/port-in/number_config_detail',
  exportPortInOrders: '/port-in/export',
  createPortInOrder: '/port-in/orders',
  updatePortInOrderConfig: (requestId: string) => `/port-in/orders/${requestId}/config`,
  updatePortInOrder: '/port-in/update-order-check',
  validatePortInPhoneNumbers: '/port-in/phone_number/validation',
  cancelPortInNumber: (requestId: string) => `/port-in/orders/${requestId}/cancel`,
  changePortInDueDate: (requestId: string) => `/port-in/orders/${requestId}/due-date`,
  getUSHolidays: '/port-in/holidays',
  downloadPortInDocument: (requestId: string, documentId: string) =>
    `/port-in/orders/${requestId}/document/${documentId}`,
  verifiedCallerId: (phoneNumber: string) => `/plivo-api/VerifiedCallerId/${phoneNumber}/`,
  deleteUser: '/admin/delete-account',
  listDestinations: (uuid?: string) =>
    `/orgs/destination${uuid ? `/${uuid}` : '?transfer_type=phlo'}`,
  senderIds: (senderId?: string) => `/accountnumbers/sender-id${senderId ? `/${senderId}` : ''}`,
  senderIdV2Base: '/message-router/senderids',
  senderIdV2CountryOptions: '/message-router/senderids/country-options',
  senderIdV2ById: (senderId: string) => `/message-router/senderids/${senderId}`,

  // Events
  loginEvent: '/orgs/event/AGENT_LOGGED_IN/invoke',
  logoutEvent: '/orgs/event/AGENT_LOGGED_OUT/invoke',
  getOrUpdateOrDeleteChatApp: (uuid?: string) => `/application/chat${uuid ? `/${uuid}` : ''}`,
  getAIAssistants: '/assistants',
  listOrCreateChatApps: '/application/chat',
  getAuthIdAndToken: '/orgs/auth-id-auth-token',
  getDemoQuestions: (appId: string) => `/ai-assist/aiassistant/demo/questions/${appId}`,
  updateDemoQuestions: () => `/ai-assist/aiassistant/demo/questions`,
  downloadFile: (fileName: string) => `/views/download/${fileName}`,

  // Compliance v2 endpoints (Plivo public API)
  complianceV2Base: '/phonenumbers/compliance/',
  complianceV2Detail: (id: string) => `/phonenumbers/compliance/${id}`,
  complianceV2Update: (id: string) => `/phonenumbers/compliance/${id}/update`,
  complianceV2Requirements: '/phonenumbers/compliance/requirements',
  complianceV2Link: '/phonenumbers/compliance/link',
  complianceV2IndiaCaf: '/phonenumbers/compliance/india_caf',
  complianceV2IndiaCafUpdate: '/phonenumbers/compliance/india_caf/update',
  complianceV2IndiaCafDocument: (documentId: string) =>
    `/phonenumbers/compliance/india_caf/documents/${documentId}`,
  complianceV2IndiaCafBusinessVerification:
    '/phonenumbers/compliance/india_caf/business_verification',
  complianceV2IndiaCafBusinessVerificationForm: (verificationId: number) =>
    `/phonenumbers/compliance/india_caf/business_verification/${verificationId}/form`,

  // Permissions
  getPermissions: `/accounts/permissions`,

  // WhatsApp
  preVerifiedNumbers: () => `/whatsapp/pre-verified-numbers`,
  verifyOtp: () => `/whatsapp/pre-verified-numbers/verify`,
  deregisterWhatsappNumber: (phoneNumberId: string) =>
    `/whatsapp/pre-verified-numbers/deregister/${phoneNumberId}`,
  syncWhatsappNumbers: () => `/whatsapp/pre-verified-numbers/sync`,
  wabaOnboarding: () => `/waba/onboarding`,
  wabaUpdate: (wabaId: string) => `/waba/update/${wabaId}`,

  // Security Screen
  generateRecoveryCodes: '/accounts/recovery-codes',
  getTwoFADetails: '/accounts/two-fa/details',
  getTwoFaPhoneDetails: '/accounts/two-fa/phone-details',
  authenticator: '/accounts/authenticator-app',
  authenticatorVerify: '/accounts/authenticator-app/validate',
  verifyUser: '/user/verify-user',
  verifyUserTwoFaOtp: '/user/verify-user-two-fa-otp',
  sendOtpForPhoneNumberChange: '/accounts/change-number/send-otp',
  verifyOtpForPhoneNumberChange: '/accounts/change-number/verify-otp',

  // Subaccounts
  listSubaccounts: '/plivo-api/Subaccount/',
  listSubaccountsInternal: '/subaccounts',
  createSubaccount: '/plivo-api/Subaccount/',
  updateSubaccount: (authId: string) => `/plivo-api/Subaccount/${authId}/`,
  deleteSubaccount: (authId: string) => `/plivo-api/Subaccount/${authId}/`,
  regenerateSubaccountAuthToken: (authId: string) => `/subaccount/${authId}/auth-token/regenerate`,
  deleteSubaccountAuthToken: (authId: string) => `/subaccount/${authId}/auth-token`,
  regenerateAccountAuthToken: '/account/auth-token/regenerate',
  deleteAccountAuthToken: '/account/auth-token',
  getSubaccount: (subAuthId: string) => `/plivo-api/Subaccount/${subAuthId}/`,

  // Applications via Plivo API Proxy
  listApplications: '/plivo-api/Application/',
  getApplication: (appId: string) => `/plivo-api/Application/${appId}/`,

  // Internal Application API via Trace Service Proxy
  listInternalApplications: '/internal/Application',

  // Plivo Verification via Hodor Proxy
  initiatePlivoVerification: '/plivo-api/VerifiedCallerId/',
  verifyPlivoCallerId: (verificationUuid: string) =>
    `/plivo-api/VerifiedCallerId/Verification/${verificationUuid}/`,
  createApplication: '/plivo-api/Application/',
  updateApplication: (appId: string) => `/plivo-api/Application/${appId}/`,
  deleteApplication: (appId: string) => `/plivo-api/Application/${appId}/`,

  // SIP Trunks via Plivo API Proxy
  listTrunks: '/plivo-api/Zentrunk/Trunk/',
  createTrunk: '/plivo-api/Zentrunk/Trunk/',
  getTrunk: (trunkId: string) => `/plivo-api/Zentrunk/Trunk/${trunkId}/`,
  updateTrunk: (trunkId: string) => `/plivo-api/Zentrunk/Trunk/${trunkId}/`,
  deleteTrunk: (trunkId: string) => `/plivo-api/Zentrunk/Trunk/${trunkId}/`,
  // IP Access Control List endpoints
  ipAccessControlLists: '/plivo-api/Zentrunk/IPAccessControlList/',
  ipAccessControlList: (ipacl_uuid: string) =>
    `/plivo-api/Zentrunk/IPAccessControlList/${ipacl_uuid}/`,

  // Credential endpoints
  credentials: '/plivo-api/Zentrunk/Credential/',
  credential: (credential_uuid: string) => `/plivo-api/Zentrunk/Credential/${credential_uuid}/`,

  // Origination URI endpoints
  originationURIs: '/plivo-api/Zentrunk/URI/',
  originationURI: (uri_uuid: string) => `/plivo-api/Zentrunk/URI/${uri_uuid}/`,
  // Voice Endpoints
  endpointBase: '/plivo-api/Endpoint/',
  endpointDetails: (endpointId: string) => `/plivo-api/Endpoint/${endpointId}/`,
  // Agent binding on an endpoint lives in contacto-core (v2), like /accountnumbers
  endpointRouting: (endpointId: string) => `/endpoints/${endpointId}`,

  // Rental Summary
  rentalSummary: '/accountnumbers/rental-summary',

  // Unrented Numbers
  unrentedNumbers: '/accountnumbers/unrented',

  // Number Profile
  allowedBuyCountries: '/numberprofile/allowed-countries',

  // Phone Numbers via Plivo API Proxy
  listNumbers: '/plivo-api/Number/',

  // Lookup API via Plivo API Proxy
  lookupNumber: (phoneNumber: string) => `/plivo-api/Lookup/Number/${phoneNumber}`,

  // Zentrunk entitlement check
  zentrunkAccountEnabled: '/zentrunk/account/enabled',

  // SIP Inbound Trunks via Core API
  listInboundTrunksInternal: '/zentrunk/inbound/trunks',
  getInboundTrunkById: (trunkId: string | number) => `/zentrunk/inbound/trunks/${trunkId}`,
  getZentrunkURIs: '/zentrunk/trunk/uris',
  createZentrunkURI: '/zentrunk/trunk/uris',
  updateZentrunkURI: (uriId: string | number) => `/zentrunk/trunk/uris/${uriId}`,
  deleteZentrunkURI: (uriId: string | number) => `/zentrunk/trunk/uris/${uriId}`,
  // Which SIP platform a trunk was set up for. Not part of the Plivo trunk
  // record (it has no field for it), so core keeps it: the platform decides what
  // configuration is legal, and that has to hold when editing too.
  sipTrunkPlatforms: '/zentrunk/trunk/platforms',
  sipTrunkPlatform: (trunkRef: string) => `/zentrunk/trunk/platforms/${trunkRef}`,
  linkInboundTrunkNumber: '/zentrunk/trunk/didmappings',
  unlinkInboundTrunkNumber: (phoneNumber: string) => `/zentrunk/trunk/didmappings/${phoneNumber}`,

  // SMS Delivery Alerts
  smsAlertSettings: '/sms/settings/alerts',

  // SMS Other Settings
  smsOtherSettings: '/sms/settings/other',

  // Numbers Other Settings
  numbersOtherSettings: '/numbers/settings/other',

  // Per-number CNAM (US-only): enable/disable CNAM Lookup + set Caller-ID NAME
  numberCnamLookup: '/accountnumbers/cnam-lookup',
  numberCnam: '/accountnumbers/cnam',
  // Bulk unrent (release) selected purchased numbers
  accountNumbersBulkDelete: '/accountnumbers/bulk-delete',

  // Voice Settings
  voiceSettings: '/voice/settings',

  // Zentrunk file capture consent (SIP Trunking Settings)
  zentrunkFileCaptureConsent: '/zentrunk/file-capture-consent',

  // Onboarding
  completeOnboarding: '/orgs/onboarding',

  // IP Whitelisting
  ipWhitelist: '/orgs/ip-whitelist',
  deleteIpWhitelist: (cidrId: string) => `/orgs/ip-whitelist/${cidrId}`,
}

interface SettingsService {
  getSupportLogin: (params: string) => Promise<ApiResponse<{ url: string }>>
  getPylonLogin: (params: string) => Promise<ApiResponse<{ url: string }>>
  getUsersList: (params: Record<string, string>) => Promise<ApiResponse<RUsersList>>
  updateUser: (uuid: string, payload: RUserUpdate) => Promise<ApiResponse<RUsersList>>
  getBusinessHours: (params: Record<string, string>) => Promise<ApiResponse<RBusinessHoursList>>
  getBusinessHourDetails: (uuid: string) => Promise<ApiResponse<RBusinessHoursPayload>>
  updateBusinessHours: (
    uuid: string,
    payload: RBusinessHoursPayload,
  ) => Promise<ApiResponse<RBusinessHoursList>>
  deleteBusinessHours: (uuid: string) => Promise<ApiResponse<RBusinessHoursList>>
  getHolidayGroups: (params?: Record<string, string>) => Promise<ApiResponse<RHolidayGroupsList>>
  getHolidaysList: (uuid: string) => Promise<ApiResponse<RHolidayDetails>>
  createHoliday: (payload: HolidayFormFields) => Promise<ApiResponse<RHolidayCreateResponse>>
  deleteHoliday: (uuid: string) => Promise<ApiResponse<RHolidayCreateResponse>>
  getTeams: (params: Record<string, string>) => Promise<ApiResponse<RTeamsList>>
  getRoles: () => Promise<ApiResponse<RRolesList>>
  getSkills: (params: Record<string, string>) => Promise<ApiResponse<RSkillsList>>
  validateSkills: (uuid: string, payload: RSkill) => Promise<ApiResponse<RSkill>>
  upsertSkills: (payload: RSkill) => Promise<ApiResponse<RSkillsList>>
  deleteSkill: (uuid: string) => Promise<ApiResponse<RSkillsList>>
  getDispositions: (type: boolean) => Promise<ApiResponse<RDispositionList>>
  getStatus: () => Promise<ApiResponse<RStatusList>>
  upsertStatus: (payload: AgentStatusFormFields) => Promise<ApiResponse<RStatusList>>
  markInactive: () => Promise<ApiResponse<ROrgStatus>>
  getStatusConfigurations: () => Promise<ApiResponse<RStatusConfigurations>>
  updateStatusConfigurations: (
    payload: RStatusConfigurations,
  ) => Promise<ApiResponse<RStatusConfigurations>>
  reorderStatus: (statuses: string[]) => Promise<ApiResponse<ROrgStatus[]>>
  deleteStatus: (uuid: string) => Promise<ApiResponse<RStatusList>>
  reorderDisposition: (disposition_ids: string[]) => Promise<ApiResponse<RDisposition[]>>
  updateDisposition: (
    queryParam: string,
  ) => (dispositionData: RDisposition) => Promise<ApiResponse<RDisposition[]>>
  searchAvailableNumbers: (params: Record<string, string>) => Promise<ApiResponse<RNumberSearch[]>>
  buyNumber: (
    number: string,
    complianceApplicationId?: string,
    haEnabled?: boolean,
    subAccount?: string,
    rerent?: boolean,
  ) => Promise<ApiResponse<RPhoneNumber>>
  deleteNumber: (number: string) => Promise<ApiResponse<RPhoneNumber>>
  unlinkNumberFromApplication: (phoneNumber: string) => Promise<ApiResponse<void>>
  listFlows: () => Promise<ApiResponse<RFlows[]>>
  listFlowsByType: (type?: string) => Promise<ApiResponse<RFlows[]>>
  listFlowOptions: (params?: Record<string, unknown>) => Promise<ApiResponse<RFlowOptionsPayload>>
  duplicateFlow: (id: string, newFlowName: string) => Promise<ApiResponse<RDuplicateFlow>>
  deleteFlow: (id: string) => Promise<ApiResponse<RFlows[]>>
  publishFlow: (id: string) => Promise<ApiResponse<RFlows[]>>
  updateFlow: (id: string, data: { name: string }) => Promise<ApiResponse<RFlows>>
  listQueues: () => Promise<ApiResponse<RQueue[]>>
  listQueuesPaginated: (params?: {
    page?: number
    limit?: number
  }) => Promise<ApiResponse<RQueue[]>>
  getQueueDetails: (uuid: string) => Promise<ApiResponse<RQueue>>
  updateQueueState: (uuid: string, state: string) => Promise<ApiResponse<RQueue>>
  updateQueue: (uuid: string, payload: Partial<RQueue>) => Promise<ApiResponse<RQueue>>
  upsertQueue: (queue: Partial<RQueue>) => Promise<ApiResponse<RQueue>>
  deleteQueue: (uuid: string) => Promise<ApiResponse<RQueue>>
  listNonTeamMembers: (params: Record<string, string>) => Promise<ApiResponse<RNonTeamMembersList>>
  listTimezones: () => Promise<ApiResponse<RTimezonesList>>
  getProfilePermissions: () => Promise<ApiResponse<RProfilePermissions>>
  updateProfilePermissions: (
    payload: RProfilePermissions,
  ) => Promise<ApiResponse<RProfilePermissions>>
  getRoutingSettings: () => Promise<ApiResponse<RRoutingSettings>>
  updateRoutingSettings: (payload: RRoutingSettings) => Promise<ApiResponse<RRoutingSettings>>
  getChannelConfiguration: (channel: string) => Promise<ApiResponse<RChannelConfiguration>>
  getAgentSettings: () => Promise<ApiResponse<RAgentSettings>>
  getAccountNumbers: (params: Record<string, unknown>) => Promise<ApiResponse<RPhoneNumber[]>>
  attachNumberToAgent: (
    digits: string,
    payload: RAgentNumberPayload,
  ) => Promise<ApiResponse<RPhoneNumber>>
  getPhoneNumbers: (params: Record<string, unknown>) => Promise<ApiResponse<RPhoneNumbersPaginated>>
  exportPhoneNumbers: (filters: Record<string, string>) => Promise<Blob>
  getProfile: () => Promise<ApiResponse<RUserProfile>>
  listOrganizations: () => Promise<ApiResponse<ROrganizationLite[]>>
  switchOrganizationV2: (
    payload: RSwitchOrganizationPayloadV2,
  ) => Promise<ApiResponse<RLoginResponse>>
  updateProfile: (payload: RUserProfilePayload) => Promise<ApiResponse<RUserProfile>>
  getKeyContacts: () => Promise<ApiResponse<RKeyContacts>>
  updateKeyContacts: (payload: RKeyContactsPayload) => Promise<ApiResponse<RKeyContacts>>
  updateOrgName: (payload: { name: string }) => Promise<ApiResponse<unknown>>
  updateChannelConfiguration: (
    channel: string,
    payload: RChannelConfiguration,
  ) => Promise<ApiResponse<RChannelConfiguration>>
  listInternalNumbers: (params: Record<string, string>) => Promise<ApiResponse<RInternalNumber[]>>
  addInternalNumber: (payload: InternalNumberField) => Promise<ApiResponse<RInternalNumber>>
  deleteInternalNumber: (contact_id: number) => Promise<ApiResponse<RInternalNumber>>
  updateInternalNumber: (
    contact_id: number,
    payload: InternalNumberField,
  ) => Promise<ApiResponse<RInternalNumber>>
  getConversationSettings: () => Promise<ApiResponse<RConversationSettings>>
  updateConversationSettings: (
    payload: RConversationSettings,
  ) => Promise<ApiResponse<RConversationSettings>>
  updatePhoneNumberConfiguration: (
    number: string,
    payload: ConfigureNumberForm,
  ) => Promise<ApiResponse<ConfigureNumberForm>>
  deleteUser: (payload: RUserDelete) => Promise<ApiResponse<RUserDelete>>
  deleteTeam: (uuid: string) => Promise<ApiResponse<RTeamsList>>
  createTeam: (payload: RTeamCreatePayload) => Promise<ApiResponse<RTeamsList>>
  updateTeam: (uuid: string, payload: RTeamUpdatePayload) => Promise<ApiResponse<RTeamsList>>
  getDestinations: (params: Record<string, string>) => Promise<ApiResponse<RDestinationsList>>
  upsertDestination: (payload: RDestination) => Promise<ApiResponse<RDestinationsList>>
  deleteDestination: (uuid: string) => Promise<ApiResponse<RDestinationsList>>
  getSenderIds: (params: Record<string, string>) => Promise<ApiResponse<RPhoneNumber[]>>
  updateSenderId: (
    senderId: string,
    payload: { name_tag: string },
  ) => Promise<ApiResponse<RPhoneNumber>>
  deleteSenderId: (senderId: string) => Promise<ApiResponse<void>>
  getSenderIdCountryOptions: () => Promise<ApiResponse<RSenderIdCountryOptions>>
  listSenderIdsV2: (params: Record<string, unknown>) => Promise<ApiResponse<RSenderIdListResponse>>
  registerSenderId: (payload: RSenderIdRegisterPayload) => Promise<ApiResponse<RSenderId>>
  setDefaultSenderId: (senderId: string, isDefault: boolean) => Promise<ApiResponse<RSenderId>>
  getSandboxNumbers: () => Promise<ApiResponse<RSandboxNumbers>>
  addSandboxNumber: (payload: RSandboxAddPayload) => Promise<ApiResponse<RSandboxAddResponse>>
  verifySandboxNumber: (
    number: string,
    payload: { code: string },
  ) => Promise<ApiResponse<RSandboxVerifyResponse>>
  resendSandboxNumberOtp: (
    number: string,
    payload: { channel?: SandboxOtpChannel },
  ) => Promise<ApiResponse<RSandboxAddResponse>>
  deleteSandboxNumber: (number: string) => Promise<ApiResponse<void>>
  getVerifiedCallerIds: (
    params?: Record<string, string>,
  ) => Promise<ApiResponse<RVerifiedCallerIds>>
  getVerifiedCallerId: (number: string) => Promise<ApiResponse<RVerifiedCallerId>>
  updateVerifiedCallerId: (
    number: string,
    payload: { alias: string; subaccount?: string },
  ) => Promise<ApiResponse<RVerifiedCallerId>>
  deleteVerifiedCallerId: (number: string) => Promise<ApiResponse<void>>

  // Events
  loginEvent: () => Promise<ApiResponse<ROrgStatus>>
  logoutEvent: () => Promise<ApiResponse<void>>
  updateAgentStatus: (payload: RAgentStatusUpdate) => Promise<ApiResponse<RAgentStatusUpdate>>
  listChatApps: (
    params: Record<string, string>,
  ) => Promise<ApiResponse<{ applications: RChatAppListItem[]; total: number }>>
  getAIAssistants: () => Promise<ApiResponse<RAIAssistant[]>>
  getChatApp: (uuid: string) => Promise<ApiResponse<RChatApp>>
  createChatApp: (payload: RChatAppPayload) => Promise<ApiResponse<RChatApp>>
  updateChatApp: (uuid: string, payload: RChatAppPayload) => Promise<ApiResponse<RChatApp>>
  deleteChatApp: (uuid: string) => Promise<ApiResponse<void>>
  getAuthIdAndToken: () => Promise<ApiResponse<RAuthDetails>>
  regenerateAccountAuthToken: (payload: {
    expire_time: number
  }) => Promise<ApiResponse<RAuthDetails>>
  deleteAccountAuthToken: () => Promise<ApiResponse<void>>
  getDemoQuestions: (appId: string) => Promise<ApiResponse<{ questions: string[] }>>
  updateDemoQuestions: (payload: RDemoQuestions) => Promise<ApiResponse<string[]>>
  pauseFlow: (flowId: string) => Promise<ApiResponse<void>>
  resumeFlow: (flowId: string) => Promise<ApiResponse<void>>
  downloadFile: (fileName: string) => Promise<ApiResponse<{ download_link: string }>>

  // Compliance v2 methods (Plivo public API)
  createComplianceApplicationV2: (formData: FormData) => Promise<ComplianceV2CreateResponse>
  getComplianceApplicationV2: (
    complianceId: string,
    expand?: string,
  ) => Promise<ComplianceV2ApplicationResponse>
  updateComplianceApplicationV2: (
    complianceId: string,
    formData: FormData,
  ) => Promise<ApiResponse<unknown>>
  getComplianceRequirementsV2: (
    params: ComplianceV2RequirementsRequest,
  ) => Promise<ComplianceV2RequirementsResponse>
  getComplianceApplicationsV2: (
    params?: Record<string, string>,
  ) => Promise<ComplianceV2ApplicationsListResponse>
  deleteComplianceApplicationV2: (request: { complianceId: string }) => Promise<ApiResponse<void>>
  linkComplianceNumbers: (payload: {
    numbers: Array<{ number: string; compliance_application_id: string }>
  }) => Promise<ApiResponse<unknown>>
  /** India CAF for the Business Details card. 404s when the account has none. */
  getIndiaCaf: () => Promise<ComplianceV2IndiaCafResponse>
  getIndiaCafDocument: (documentId: string) => Promise<Blob>
  /**
   * The new document, in one call. Core stores it as a DRAFT application and
   * forwards it -- deliberately never submitting, because submitting is what
   * notifies the compliance workflow and a CAF update is not a KYC filing.
   */
  uploadIndiaCafUpdate: (formData: FormData) => Promise<ComplianceV2IndiaCafUpdateResponse>
  /** The signed and stamped yearly verification form, as the multipart field `file`. */
  submitIndiaCafBusinessVerification: (
    formData: FormData,
  ) => Promise<ComplianceV2IndiaCafBusinessVerificationResponse>
  getIndiaCafBusinessVerificationForm: (verificationId: number) => Promise<Blob>

  // Permissions
  getPermissions: () => Promise<ApiResponse<RPermissions>>

  // WhatsApp
  preVerifiedNumbers: (
    phoneNumber: string,
    forwardedPhoneNumber: string,
    otpMethod: 'sms' | 'voice',
    numberSource: string,
  ) => Promise<ApiResponse<WhatsAppPreVerifiedResponse>>
  verifyOtp: (
    phoneNumber: string,
    code: string,
    phoneNumberId: string,
    sessionUUID: string,
  ) => Promise<ApiResponse<WhatsAppVerifyOtpResponse>>
  deregisterWhatsappNumber: (phoneNumberId: string) => Promise<ApiResponse<void>>
  syncWhatsappNumbers: () => Promise<ApiResponse<void>>
  wabaOnboarding: (payload: WabaOnboardingRequest) => Promise<ApiResponse<WabaOnboardingResponse>>
  wabaUpdate: (
    wabaId: string,
    payload?: {
      application_url?: string
      subaccount_auth_id?: string
      log_incoming_request?: string
    },
  ) => Promise<ApiResponse<WabaUpdateResponse>>

  // Security Screen
  getTwoFADetails: () => Promise<ApiResponse<RTwoFADetailsResponse>>
  getTwoFaPhoneDetails: () => Promise<ApiResponse<RTwoFaPhoneDetailsResponse>>
  generateRecoveryCodes: (payload: Record<string, unknown>) => Promise<ApiResponse<RRecoveryCodes>>
  sendOtpForPhoneNumberChange: (
    payload: RSendOtpForPhoneNumberChangePayload,
  ) => Promise<ApiResponse<RSendOtpForPhoneNumberChangeResponse>>
  verifyOtpForPhoneNumberChange: (
    payload: RVerifyOtpForPhoneNumberChangePayload,
  ) => Promise<ApiResponse<RVerifyOtpForPhoneNumberChangeResponse>>
  addAuthenticator: (
    payload: RAuthenticatorAppPayload,
  ) => Promise<ApiResponse<RAuthenticatorAppResponse>>
  removeAuthenticator: (
    payload: RAuthenticatorAppPayload,
  ) => Promise<ApiResponse<RAuthenticatorAppResponse>>
  updateAuthenticatorToDefault: (
    payload: RAuthenticatorAppPayload,
  ) => Promise<ApiResponse<RAuthenticatorAppResponse>>
  verifyAuthenticatorCode: (
    payload: RAuthenticatorVerify,
  ) => Promise<ApiResponse<RAuthenticatorVerifyResponse>>

  verifyUser: (payload: RVerifyUserPayload) => Promise<ApiResponse<RVerifyUserResponse>>
  verifyUserTwoFaOtp: (
    payload: RVerifyUserTwoFaOtpPayload,
  ) => Promise<ApiResponse<RVerifyUserResponse>>

  // Subaccounts
  listSubaccounts: (params?: Record<string, string | unknown>) => Promise<ApiResponse<RSubaccounts>>
  listSubaccountsInternal: (
    params?: Record<string, string | unknown>,
  ) => Promise<ApiResponse<RSubaccounts>>
  createSubaccount: (payload: {
    name: string
    enabled: boolean
  }) => Promise<ApiResponse<RSubaccount>>
  updateSubaccount: (
    authId: string,
    payload: { name: string; enabled?: boolean },
  ) => Promise<ApiResponse<RSubaccount>>
  deleteSubaccount: (authId: string, cascade?: boolean) => Promise<ApiResponse<void>>
  regenerateSubaccountAuthToken: (
    authId: string,
    payload: { expire_time: number },
  ) => Promise<ApiResponse<RSubaccount>>
  deleteSubaccountAuthToken: (authId: string) => Promise<ApiResponse<void>>
  getSubaccount: (subAuthId: string) => Promise<ApiResponse<RSubaccount>>

  // Applications via Plivo API Proxy
  listApplications: (
    params?: Record<string, string | unknown>,
  ) => Promise<ApiResponse<RPlivoApplications>>
  getApplication: (appId: string) => Promise<ApiResponse<RPlivoApplication>>

  // Internal Application API via Trace Service Proxy
  listInternalApplications: (
    params?: Record<string, string>,
  ) => Promise<ApiResponse<RPlivoApplicationsInternal>>

  // Phone Numbers via Plivo API Proxy
  listNumbers: (params?: Record<string, string | unknown>) => Promise<ApiResponse<RPlivoNumbers>>

  // Lookup API
  lookupNumber: (phoneNumber: string) => Promise<ApiResponse<LookupResponse>>

  // Plivo Verification
  initiatePlivoVerification: (
    payload: InitiateCallerIdVerificationPayload,
  ) => Promise<ApiResponse<RInitiateCallerIdVerification>>
  verifyPlivoCallerId: (
    verification_uuid: string,
    payload: VerifyCallerIdPayload,
  ) => Promise<ApiResponse<RVerifyCallerId>>

  // Applications
  createApplication: (payload: Record<string, unknown>) => Promise<ApiResponse<unknown>>
  updateApplication: (
    appId: string,
    payload: Record<string, unknown>,
  ) => Promise<ApiResponse<unknown>>
  deleteApplication: (appId: string, params?: Record<string, unknown>) => Promise<ApiResponse<void>>

  // SIP Trunks via Plivo API Proxy
  listTrunks: (params?: Record<string, string>) => Promise<ApiResponse<RTrunks>>
  createTrunk: (payload: Record<string, unknown>) => Promise<ApiResponse<unknown>>
  getTrunk: (trunkId: string) => Promise<ApiResponse<unknown>>
  updateTrunk: (trunkId: string, payload: Record<string, unknown>) => Promise<ApiResponse<unknown>>
  deleteTrunk: (trunkId: string) => Promise<ApiResponse<void>>
  createIPAccessControlList: (
    payload: IPAccessControlListPayload,
  ) => Promise<ApiResponse<RCreateIPAccessMutation>>
  getIPAccessControlLists: (params?: {
    limit?: number
    offset?: number
  }) => Promise<ApiResponse<RIPAccessControlList>>
  getIPAccessControlList: (ipacl_uuid: string) => Promise<ApiResponse<IPAccessControlList>>
  updateIPAccessControlList: (
    ipacl_uuid: string,
    payload: IPAccessControlListPayload,
  ) => Promise<ApiResponse<RCreateIPAccessMutation>>
  deleteIPAccessControlList: (ipacl_uuid: string) => Promise<ApiResponse<void>>
  createCredential: (payload: CredentialPayload) => Promise<ApiResponse<RCredentialMutation>>
  getCredentials: (params?: {
    limit?: number
    offset?: number
  }) => Promise<ApiResponse<RCredential>>
  getCredential: (credential_uuid: string) => Promise<ApiResponse<RCredential>>
  updateCredential: (
    credential_uuid: string,
    payload: CredentialPayload,
  ) => Promise<ApiResponse<RCredentialMutation>>
  deleteCredential: (credential_uuid: string) => Promise<ApiResponse<void>>
  createOriginationURI: (
    payload: OriginationURIPayload,
  ) => Promise<ApiResponse<ROriginationURIMutation>>
  getOriginationURIs: (params?: {
    limit?: number
    offset?: number
  }) => Promise<ApiResponse<ROriginationURI>>
  getOriginationURI: (uri_uuid: string) => Promise<ApiResponse<RPublicOriginationURI>>
  updateOriginationURI: (
    uri_uuid: string,
    payload: Partial<OriginationURIPayload>,
  ) => Promise<ApiResponse<ROriginationURIMutation>>
  deleteOriginationURI: (uri_uuid: string) => Promise<ApiResponse<void>>
  // Voice Endpoints
  listEndpoints: (params: Record<string, string>) => Promise<ApiResponse<RListEndpoints>>
  getEndpoint: (endpointId: string) => Promise<ApiResponse<REndpoint>>
  createEndpoint: (payload: CreateEndpointPayload) => Promise<ApiResponse<REndpoint>>
  updateEndpoint: (
    endpointId: string,
    payload: UpdateEndpointPayload,
  ) => Promise<ApiResponse<REndpoint>>
  deleteEndpoint: (endpointId: string) => Promise<ApiResponse<void>>
  updateEndpointRouting: (
    endpointId: string,
    payload: UpdateEndpointRoutingPayload,
  ) => Promise<ApiResponse<REndpointRouting>>

  // Rental Summary
  getRentalSummary: (params: Record<string, string>) => Promise<ApiResponse<RListRentalSummary>>

  // Unrented Numbers
  getUnrentedNumbers: (params: Record<string, string>) => Promise<ApiResponse<RListUnrentedNumbers>>

  // Number Profile
  getAllowedBuyCountries: () => Promise<ApiResponse<RAllowedBuyCountries>>

  // Numbers Other Settings
  getNumbersOtherSettings: () => Promise<ApiResponse<RNumbersOtherSettings>>
  updateNumbersOtherSettings: (payload: RNumbersOtherSettings) => Promise<ApiResponse<unknown>>

  // Per-number CNAM (US-only). Both accept a list so single-edit sends a 1-element array.
  updateNumberCnamLookup: (payload: {
    numbers: string[]
    cnam_lookup: boolean
  }) => Promise<ApiResponse<RNumberCnamUpdate>>
  updateNumberCnam: (payload: {
    numbers: string[]
    cnam: string
  }) => Promise<ApiResponse<RNumberCnamUpdate>>

  // Bulk unrent (release) selected purchased numbers in one request
  bulkDeleteNumbers: (payload: { numbers: string[] }) => Promise<ApiResponse<RNumberBulkDelete>>

  // Zentrunk entitlement check
  getZentrunkAccountEnabled: () => Promise<ApiResponse<{ enabled: boolean }>>

  // SIP Inbound Trunks via Core API
  listInboundTrunksInternal: (params?: Record<string, string>) => Promise<ApiResponse<RTrunks>>
  getInboundTrunkById: (trunkId: string | number) => Promise<ApiResponse<RTrunk>>
  createInboundTrunkInternal: (payload: {
    name: string
    enabled: boolean
    primary_uri: number
    fallback_uri?: number
    message_url?: string
    message_method?: string
    linked_numbers?: string[] // Backend will fetch number details and link them
  }) => Promise<ApiResponse<RTrunk>>
  updateInboundTrunkInternal: (
    trunkId: string | number,
    payload: {
      name: string
      enabled: boolean
      primary_uri: number
      fallback_uri?: number
      message_url?: string
      message_method?: string
      linked_numbers?: string[] // Backend will fetch number details and link them
    },
  ) => Promise<ApiResponse<RTrunk>>
  getSipTrunkPlatforms: () => Promise<ApiResponse<RSipTrunkPlatforms>>
  recordSipTrunkPlatform: (
    trunkRef: string,
    payload: { platform: string; trunk_direction: 'inbound' | 'outbound' },
  ) => Promise<ApiResponse<RSipTrunkPlatform>>
  forgetSipTrunkPlatform: (trunkRef: string) => Promise<ApiResponse<unknown>>
  getZentrunkURIs: (
    params?: Record<string, string>,
  ) => Promise<ApiResponse<ROriginationURIInternal[]>>
  createZentrunkURI: (payload: {
    name: string
    uri: string
    sip_user?: string
    username?: string
    password?: string
  }) => Promise<ApiResponse<ROriginationURIInternal>>
  updateZentrunkURI: (
    uriId: string | number,
    payload: {
      name: string
      uri: string
      sip_user?: string
      username?: string
      password?: string
    },
  ) => Promise<ApiResponse<ROriginationURIInternal>>
  deleteZentrunkURI: (uriId: string | number) => Promise<ApiResponse<void>>
  linkInboundTrunkNumber: (payload: {
    trunk_id: number | string
    number: string
    country_iso?: string
    type?: string
    sms_enabled?: boolean
    voice_enabled?: boolean
  }) => Promise<ApiResponse<unknown>>
  unlinkInboundTrunkNumber: (phoneNumber: string) => Promise<ApiResponse<unknown>>
  listPortInOrders: (params: Record<string, string>) => Promise<ApiResponse<RListPortInOrders>>
  getPortInOrderDetails: (requestId: string) => Promise<ApiResponse<PortInOrderDetails>>
  getPortInNumberConfigDetails: (requestId: string) => Promise<
    ApiResponse<
      Array<{
        number: string
        alias_name?: string
        cname?: string
        application_type?: string
        plivo_app?: string
        phlo_app?: string
        sub_account_detail?: string
        blocked?: boolean
        sms_capable?: boolean
        voice_capable?: boolean
      }>
    >
  >
  exportPortInOrders: (params: Record<string, string>) => Promise<ApiResponse<unknown>>
  createPortInOrder: (formData: FormData) => Promise<ApiResponse<{ request_id: string }>>
  updatePortInOrderConfig: (
    requestId: string,
    numberConfigInfo: Array<{
      number: string
      alias: string
      cnam?: string
      application?: string
      capabilities: string
    }>,
  ) => Promise<ApiResponse<unknown>>
  updatePortInOrder: (
    requestId: string,
    numbers: string,
    formData: FormData,
    updateMapForm: string,
  ) => Promise<ApiResponse<unknown>>
  cancelPortInNumber: (
    requestId: string,
    phoneNumber: string,
  ) => Promise<ApiResponse<{ message: string }>>
  changePortInDueDate: (
    requestId: string,
    phoneNumber: string,
    desiredDueDate: string,
  ) => Promise<ApiResponse<{ message: string }>>
  validatePortInPhoneNumbers: (
    phoneNumbers: string[],
    requestId?: string,
  ) => Promise<
    ApiResponse<
      Record<
        string,
        { status_code: number; country_iso?: string; number_type?: string; error_message?: string }
      >
    >
  >
  getUSHolidays: () => Promise<ApiResponse<string[]>>
  downloadPortInDocument: (requestId: string, documentId: string) => Promise<Blob>

  // SMS Delivery Alerts
  getSmsAlertSettings: () => Promise<ApiResponse<RSmsAlertSettings>>
  updateSmsAlertSettings: (payload: RSmsAlertSettings) => Promise<ApiResponse<RSmsAlertSettings>>

  // SMS Other Settings
  getSmsOtherSettings: () => Promise<ApiResponse<RSmsOtherSettings>>
  updateSmsOtherSettings: (payload: RSmsOtherSettings) => Promise<ApiResponse<RSmsOtherSettings>>

  // Voice Settings
  getVoiceSettings: (subaccountAuthId?: string) => Promise<ApiResponse<RVoiceSettings>>
  updateVoiceSettings: (payload: UpdateVoiceSettingPayload) => Promise<ApiResponse<RVoiceSettings>>

  // Zentrunk file capture consent (SIP Trunking Settings)
  getZentrunkFileCaptureConsent: () => Promise<ApiResponse<RZentrunkFileCaptureConsent>>
  updateZentrunkFileCaptureConsent: (
    payload: UpdateZentrunkFileCaptureConsentPayload,
  ) => Promise<ApiResponse<RZentrunkFileCaptureConsent>>

  // Onboarding
  completeOnboarding: (payload: OnboardingFormData) => Promise<ApiResponse<void>>

  // IP Whitelisting
  listIPWhitelist: (
    params?: Record<string, string>,
  ) => Promise<ApiResponse<Record<string, unknown>>>
  addIPWhitelist: (payload: { cidrs: string[] }) => Promise<ApiResponse<unknown>>
  deleteIPWhitelist: (cidrId: string) => Promise<ApiResponse<void>>

  // Account Concurrency (IN region)
  getAccountConcurrency: () => Promise<ApiResponse<RAccountConcurrency>>
  getSipCredentials: () => Promise<ApiResponse<RSipCredentials>>

  // Account Usage Limit (account-service)
  getUsageLimit: (accountId: string) => Promise<ApiResponse<RUsageLimit>>
  createUsageLimit: (
    accountId: string,
    payload: UsageLimitPayload,
  ) => Promise<ApiResponse<RUsageLimit>>
  updateUsageLimit: (
    accountId: string,
    payload: UsageLimitPayload,
  ) => Promise<ApiResponse<RUsageLimit>>
}

const serviceFactory = (): SettingsService => {
  const service = createService('', true, BackendServiceName.core, 'v1')
  const serviceV2 = createService('', true, BackendServiceName.core, 'v2')
  const defaultServiceV1 = createService('', true, BackendServiceName.default, 'v1')
  const defaultGlobalServiceV1 = createService('', false, BackendServiceName.default, 'v1')
  const accountService = createService('/account-service', true, BackendServiceName.billing, 'v1')

  return {
    getSupportLogin: (params) =>
      defaultGlobalServiceV1.get(
        `/accounts${SETTINGS_ENDPOINTS.supportLogin}${params ? `?${params}` : ''}`,
      ),
    getPylonLogin: (params) =>
      defaultGlobalServiceV1.get(
        `/accounts${SETTINGS_ENDPOINTS.pylonLogin}${params ? `?${params}` : ''}`,
      ),
    downloadFile: (fileName) => service.get(SETTINGS_ENDPOINTS.downloadFile(fileName)),
    getUsersList: async (params) => serviceV2.get(SETTINGS_ENDPOINTS.listOrgMembers(), params),
    deleteUser: async (payload) => service.post(SETTINGS_ENDPOINTS.deleteUser, payload),
    getBusinessHours: (params) => service.get(SETTINGS_ENDPOINTS.businessHours(), params),
    getBusinessHourDetails: (uuid) => service.get(SETTINGS_ENDPOINTS.businessHours(uuid)),
    updateBusinessHours: (uuid, payload) =>
      service.post(SETTINGS_ENDPOINTS.businessHours(uuid), payload),
    deleteBusinessHours: (uuid) => service.delete(SETTINGS_ENDPOINTS.businessHours(uuid)),
    getHolidayGroups: (params) => service.get(SETTINGS_ENDPOINTS.listHolidayGroups(), params),
    getHolidaysList: (uuid) => service.get(SETTINGS_ENDPOINTS.listHolidayGroups(uuid)),
    createHoliday: (payload) => service.post(SETTINGS_ENDPOINTS.listHolidays(), payload),
    deleteHoliday: (uuid) => service.delete(SETTINGS_ENDPOINTS.listHolidays(uuid)),
    getTeams: (params) => service.get(SETTINGS_ENDPOINTS.listTeams(), params),
    getRoles: () => service.get(SETTINGS_ENDPOINTS.listRoles),
    deleteTeam: (uuid) => service.delete(SETTINGS_ENDPOINTS.listTeams(uuid)),
    createTeam: (payload) => service.post(SETTINGS_ENDPOINTS.listTeams(), payload),
    updateTeam: (uuid, payload) => service.post(SETTINGS_ENDPOINTS.listTeams(uuid), payload),
    getSkills: (params) => service.get(SETTINGS_ENDPOINTS.listSkills(), params),
    validateSkills: (uuid, payload) =>
      service.post(SETTINGS_ENDPOINTS.validateSkills(uuid), payload),
    upsertSkills: (skill) => service.post(SETTINGS_ENDPOINTS.listSkills(skill.id), skill),
    deleteSkill: (uuid) => service.delete(SETTINGS_ENDPOINTS.listSkills(uuid)),
    getStatus: () => service.get(SETTINGS_ENDPOINTS.status()),
    markInactive: () => service.post(SETTINGS_ENDPOINTS.markInactivity, {}),
    reorderStatus: (statuses) => service.post(SETTINGS_ENDPOINTS.reorderStaus, { statuses }),
    upsertStatus: (status) => service.post(SETTINGS_ENDPOINTS.status(status.uuid), status),
    deleteStatus: (uuid) => service.delete(SETTINGS_ENDPOINTS.status(uuid)),
    updateAgentStatus: (payload) => service.post(SETTINGS_ENDPOINTS.updateAgentStatus, payload),
    getDispositions: (type) => service.get(SETTINGS_ENDPOINTS.listDispositions(type)),
    reorderDisposition: (disposition_ids) =>
      service.post(SETTINGS_ENDPOINTS.reorderDispositions, { disposition_ids }),

    updateDisposition: (queryParam) => (dispositionData) => {
      switch (dispositionData.type) {
        case 'status':
          return service.patch(SETTINGS_ENDPOINTS.updateDisposition(queryParam), {
            disable: dispositionData.disabled,
          })
        case 'new_disposition':
          return service.post(SETTINGS_ENDPOINTS.updateDisposition(), dispositionData)
        default:
          return service.post(SETTINGS_ENDPOINTS.updateDisposition(queryParam), dispositionData)
      }
    },
    searchAvailableNumbers: (params) =>
      service.get(SETTINGS_ENDPOINTS.phoneNumbers('search'), params),
    buyNumber: (number, complianceApplicationId, haEnabled, subAccount, rerent) =>
      service.post(SETTINGS_ENDPOINTS.phoneNumbers(number), {
        ...(complianceApplicationId ? { compliance_application_id: complianceApplicationId } : {}),
        ...(haEnabled ? { ha_enable: true } : {}),
        ...(subAccount ? { sub_account: subAccount } : {}),
        ...(rerent ? { rerent: true } : {}),
      }),
    listFlows: () => service.get(SETTINGS_ENDPOINTS.listFlows()),
    listFlowsByType: (type) => service.get(SETTINGS_ENDPOINTS.listFlows(type)),
    listFlowOptions: (params) => service.get(SETTINGS_ENDPOINTS.flowOptions, params),
    publishFlow: (id) => service.get(SETTINGS_ENDPOINTS.publishFlow(id)),
    duplicateFlow: (id, newFlowName) =>
      service.post(SETTINGS_ENDPOINTS.duplicateFlow(id), { phlo_name: newFlowName }),
    deleteFlow: (id) => service.delete(SETTINGS_ENDPOINTS.deleteFlow(id)),
    updateFlow: (id, data) => service.patch(SETTINGS_ENDPOINTS.updateFlow(id), data),
    // listFlows: () =>
    //   service.post(SETTINGS_ENDPOINTS.listFlows, {
    //     filters: {},
    //     page: 1,
    //     limit: 3000,
    //   }),
    listQueues: () => service.get(SETTINGS_ENDPOINTS.listQueues()),
    listQueuesPaginated: (params) => service.get(SETTINGS_ENDPOINTS.listQueues(), params),
    getQueueDetails: (uuid) => service.get(SETTINGS_ENDPOINTS.detailedQueue(uuid)),
    updateQueueState: (uuid, state) =>
      service.post(SETTINGS_ENDPOINTS.detailedQueue(uuid, state), {}),
    updateQueue: (uuid, payload) => service.post(SETTINGS_ENDPOINTS.detailedQueue(uuid), payload),
    upsertQueue: (queue) => service.post(SETTINGS_ENDPOINTS.listQueues(), queue),
    deleteQueue: (uuid) => service.delete(SETTINGS_ENDPOINTS.detailedQueue(uuid)),
    listNonTeamMembers: (params) => service.get(SETTINGS_ENDPOINTS.listNonTeamMembers, params),
    listTimezones: () => service.get(SETTINGS_ENDPOINTS.listTimezones),
    getProfilePermissions: () => service.get(SETTINGS_ENDPOINTS.getProfilePermissions),
    updateProfilePermissions: (payload) =>
      service.post(SETTINGS_ENDPOINTS.getProfilePermissions, payload),
    getRoutingSettings: () => service.get(SETTINGS_ENDPOINTS.routingSettings),
    updateRoutingSettings: (payload) => service.post(SETTINGS_ENDPOINTS.routingSettings, payload),
    // Embed uses v1: hodor proxies only /v1/embed/contacto-core/* (list-account-numbers
    // rule), landing on core's embed twin that takes the org from the session.
    getAccountNumbers: (params) =>
      (isEmbedMode() ? service : serviceV2).get(SETTINGS_ENDPOINTS.accountNumbers(), params),
    // Embed only (hodor's attach-number rule). Core takes the org and sub-account from the
    // session, so the payload names only the number's call agent.
    attachNumberToAgent: (digits, payload) =>
      service.patch(SETTINGS_ENDPOINTS.accountNumbers(digits), payload),
    getProfile: () => service.get(SETTINGS_ENDPOINTS.profile),
    getAccountConcurrency: () => service.get(SETTINGS_ENDPOINTS.accountConcurrency),
    getSipCredentials: () => service.get(SETTINGS_ENDPOINTS.sipCredentials),
    getUsageLimit: (accountId) => accountService.get(SETTINGS_ENDPOINTS.usageLimit(accountId)),
    createUsageLimit: (accountId, payload) =>
      accountService.post(SETTINGS_ENDPOINTS.usageLimit(accountId), payload),
    updateUsageLimit: (accountId, payload) =>
      accountService.put(SETTINGS_ENDPOINTS.usageLimit(accountId), payload),
    listOrganizations: () => defaultGlobalServiceV1.get(SETTINGS_ENDPOINTS.listOrganizations),
    switchOrganizationV2: (payload) =>
      defaultGlobalServiceV1.post(SETTINGS_ENDPOINTS.switchOrganizationV2(), payload),
    updateProfile: (payload) => service.post(SETTINGS_ENDPOINTS.updateName, payload),
    getKeyContacts: () => service.get(SETTINGS_ENDPOINTS.keyContacts),
    updateKeyContacts: (payload: RKeyContactsPayload) =>
      service.post(SETTINGS_ENDPOINTS.keyContacts, payload),
    updateOrgName: (payload: { name: string }) =>
      service.patch(SETTINGS_ENDPOINTS.updateOrgName, payload),
    getChannelConfiguration: (channel) =>
      service.get(SETTINGS_ENDPOINTS.channelConfiguration(channel)),
    getAgentSettings: () => service.get(SETTINGS_ENDPOINTS.agentSettings),
    updateChannelConfiguration: (channel, payload) =>
      service.post(SETTINGS_ENDPOINTS.channelConfiguration(channel), payload),
    getPhoneNumbers: (params) => baseService.get(SETTINGS_ENDPOINTS.phoneNumbersPaginated, params),
    exportPhoneNumbers: (filters) =>
      baseService.post(SETTINGS_ENDPOINTS.exportPhoneNumbers, { filters }, undefined, {
        responseType: 'blob',
      }),
    listInternalNumbers: () => service.get(SETTINGS_ENDPOINTS.internalNumbers(), { limit: 500 }),
    addInternalNumber: (payload) => service.post(SETTINGS_ENDPOINTS.internalNumbers(), payload),
    deleteInternalNumber: (contact_id) =>
      service.delete(SETTINGS_ENDPOINTS.internalNumbers(contact_id)),
    updateInternalNumber: (contact_id, payload) =>
      service.patch(SETTINGS_ENDPOINTS.internalNumbers(contact_id), payload),
    getConversationSettings: () => service.get(SETTINGS_ENDPOINTS.conversationsConfiguration),
    updateConversationSettings: (payload) =>
      service.patch(SETTINGS_ENDPOINTS.conversationsConfiguration, payload),
    updateSenderId: (senderId, payload) =>
      service.put(SETTINGS_ENDPOINTS.senderIds(senderId), payload),
    deleteSenderId: (senderId) =>
      serviceV2.delete(`${SETTINGS_ENDPOINTS.accountNumbers(senderId)}?is_sender_id=true`),
    // Sandbox verified destinations via contacto-core -> number_service
    getSandboxNumbers: () => service.get(SETTINGS_ENDPOINTS.sandboxNumbers),
    addSandboxNumber: (payload) => service.post(SETTINGS_ENDPOINTS.sandboxNumbers, payload),
    verifySandboxNumber: (number, payload) =>
      service.post(SETTINGS_ENDPOINTS.sandboxNumberVerify(number), payload),
    resendSandboxNumberOtp: (number, payload) =>
      service.post(SETTINGS_ENDPOINTS.sandboxNumberResend(number), payload),
    deleteSandboxNumber: (number) => service.delete(SETTINGS_ENDPOINTS.sandboxNumber(number)),
    // Plivo Verified Caller IDs via Hodor Proxy
    getVerifiedCallerIds: (params) =>
      defaultServiceV1.get(SETTINGS_ENDPOINTS.verifiedCallerIds, params),
    getVerifiedCallerId: (number) =>
      defaultServiceV1.get(SETTINGS_ENDPOINTS.verifiedCallerId(number)),
    updateVerifiedCallerId: (number, payload) =>
      defaultServiceV1.post(SETTINGS_ENDPOINTS.verifiedCallerId(number), payload),
    deleteVerifiedCallerId: (number) =>
      defaultServiceV1.delete(SETTINGS_ENDPOINTS.verifiedCallerId(number)),
    loginEvent: () => service.get(SETTINGS_ENDPOINTS.loginEvent),
    logoutEvent: () => service.get(SETTINGS_ENDPOINTS.logoutEvent),
    listChatApps: (params) =>
      service.get(`${SETTINGS_ENDPOINTS.listOrCreateChatApps}?paginated=false`, params),
    getAIAssistants: () =>
      service.get(SETTINGS_ENDPOINTS.getAIAssistants, '', {
        validateStatus: (status) => status === 200 || status === 404,
      }),
    getChatApp: (uuid) => service.get(SETTINGS_ENDPOINTS.getOrUpdateOrDeleteChatApp(uuid)),
    createChatApp: (payload) => service.post(SETTINGS_ENDPOINTS.listOrCreateChatApps, payload),
    updateChatApp: (uuid, payload) =>
      service.put(SETTINGS_ENDPOINTS.getOrUpdateOrDeleteChatApp(uuid), payload),
    deleteChatApp: (uuid) => service.delete(SETTINGS_ENDPOINTS.getOrUpdateOrDeleteChatApp(uuid)),
    getPermissions: () => service.get(SETTINGS_ENDPOINTS.getPermissions),

    // v2
    getStatusConfigurations: () => serviceV2.get(SETTINGS_ENDPOINTS.statusConfigurations),
    updateStatusConfigurations: (payload) =>
      serviceV2.put(SETTINGS_ENDPOINTS.statusConfigurations, payload),
    updatePhoneNumberConfiguration: (number, payload) =>
      serviceV2.patch(SETTINGS_ENDPOINTS.accountNumbers(number), payload),
    deleteNumber: (number) => serviceV2.delete(SETTINGS_ENDPOINTS.accountNumbers(number)),
    unlinkNumberFromApplication: (phoneNumber) =>
      service.delete(SETTINGS_ENDPOINTS.unlinkNumberFromApplication(phoneNumber)),
    updateUser: async (uuid, payload) =>
      serviceV2.patch(SETTINGS_ENDPOINTS.listOrgMembers(uuid), payload),
    getDestinations: (params) => service.get(SETTINGS_ENDPOINTS.listDestinations(), params),
    upsertDestination: (destination) =>
      service.post(SETTINGS_ENDPOINTS.listDestinations(destination.uuid), destination),
    deleteDestination: (uuid) => service.delete(SETTINGS_ENDPOINTS.listDestinations(uuid)),
    getSenderIds: (params) => serviceV2.get(SETTINGS_ENDPOINTS.senderIds(), params),
    getSenderIdCountryOptions: () => serviceV2.get(SETTINGS_ENDPOINTS.senderIdV2CountryOptions),
    listSenderIdsV2: (params) => serviceV2.get(SETTINGS_ENDPOINTS.senderIdV2Base, params),
    registerSenderId: (payload) => serviceV2.post(SETTINGS_ENDPOINTS.senderIdV2Base, payload),
    setDefaultSenderId: (senderId, isDefault) =>
      serviceV2.patch(SETTINGS_ENDPOINTS.senderIdV2ById(senderId), { is_default: isDefault }),
    getAuthIdAndToken: () => service.get(SETTINGS_ENDPOINTS.getAuthIdAndToken),
    regenerateAccountAuthToken: (payload) =>
      service.post(SETTINGS_ENDPOINTS.regenerateAccountAuthToken, payload),
    deleteAccountAuthToken: () => service.delete(SETTINGS_ENDPOINTS.deleteAccountAuthToken),
    getDemoQuestions: (appId) => defaultServiceV1.get(SETTINGS_ENDPOINTS.getDemoQuestions(appId)),
    updateDemoQuestions: (payload) =>
      defaultServiceV1.post(SETTINGS_ENDPOINTS.updateDemoQuestions(), payload),
    pauseFlow: (flowId) => service.post(SETTINGS_ENDPOINTS.pauseFlow(flowId), {}),
    resumeFlow: (flowId) => service.post(SETTINGS_ENDPOINTS.resumeFlow(flowId), {}),

    preVerifiedNumbers: (phoneNumber, forwardedPhoneNumber, otpMethod, numberSource) =>
      service.post(SETTINGS_ENDPOINTS.preVerifiedNumbers(), {
        phone_number: phoneNumber,
        forwarded_phone_number: forwardedPhoneNumber,
        otp_method: otpMethod,
        number_source: numberSource,
      }),
    verifyOtp: (phoneNumber, otp, phoneNumberId, sessionUUID) =>
      service.post(SETTINGS_ENDPOINTS.verifyOtp(), {
        phone_number: phoneNumber,
        code: otp,
        pre_verified_id: phoneNumberId,
        session_uuid: sessionUUID,
      }),
    deregisterWhatsappNumber: (phoneNumberId) =>
      service.delete(SETTINGS_ENDPOINTS.deregisterWhatsappNumber(phoneNumberId)),
    syncWhatsappNumbers: () => service.post(SETTINGS_ENDPOINTS.syncWhatsappNumbers(), {}),
    wabaOnboarding: (payload) => service.post(SETTINGS_ENDPOINTS.wabaOnboarding(), payload),
    wabaUpdate: (wabaId, payload = {}) =>
      service.put(SETTINGS_ENDPOINTS.wabaUpdate(wabaId), payload),

    // Security Screen
    getTwoFADetails: () => service.get(SETTINGS_ENDPOINTS.getTwoFADetails),
    getTwoFaPhoneDetails: () =>
      service.get(SETTINGS_ENDPOINTS.getTwoFaPhoneDetails, '', {
        validateStatus: (status: number) => status === 200 || status === 404,
      }),
    verifyUser: (payload) =>
      service.post(SETTINGS_ENDPOINTS.verifyUser, payload, undefined, {
        validateStatus: (status: number) => status === 200 || status === 400,
      }),
    verifyUserTwoFaOtp: (payload) =>
      service.post(SETTINGS_ENDPOINTS.verifyUserTwoFaOtp, payload, undefined, {
        validateStatus: (status: number) => status === 200 || status === 400,
      }),
    generateRecoveryCodes: (payload: Record<string, unknown>) =>
      service.post(SETTINGS_ENDPOINTS.generateRecoveryCodes, payload),
    addAuthenticator: (payload) => service.post(SETTINGS_ENDPOINTS.authenticator, payload),
    updateAuthenticatorToDefault: (payload) =>
      service.put(SETTINGS_ENDPOINTS.authenticator, payload),
    removeAuthenticator: (payload) => service.delete(SETTINGS_ENDPOINTS.authenticator, payload),
    verifyAuthenticatorCode: (payload) =>
      service.post(SETTINGS_ENDPOINTS.authenticatorVerify, payload, undefined, {
        validateStatus: (status: number) => status === 200 || status === 400,
      }),
    sendOtpForPhoneNumberChange: (payload) =>
      service.post(SETTINGS_ENDPOINTS.sendOtpForPhoneNumberChange, payload, undefined, {
        validateStatus: (status: number) => status === 200 || status === 400,
      }),
    verifyOtpForPhoneNumberChange: (payload) =>
      service.post(SETTINGS_ENDPOINTS.verifyOtpForPhoneNumberChange, payload, undefined, {
        validateStatus: (status: number) => status === 200 || status === 400,
      }),
    // Compliance v2 methods (Plivo public API — uses serviceV2)
    createComplianceApplicationV2: (formData) =>
      serviceV2.post(SETTINGS_ENDPOINTS.complianceV2Base, formData),
    getComplianceApplicationV2: (complianceId, expand) =>
      serviceV2.get(
        SETTINGS_ENDPOINTS.complianceV2Detail(complianceId),
        expand ? { expand } : undefined,
      ),
    updateComplianceApplicationV2: (complianceId, formData) =>
      serviceV2.post(SETTINGS_ENDPOINTS.complianceV2Update(complianceId), formData),
    getComplianceRequirementsV2: (params) =>
      serviceV2.get(SETTINGS_ENDPOINTS.complianceV2Requirements, params),
    getComplianceApplicationsV2: (params) =>
      serviceV2.get(SETTINGS_ENDPOINTS.complianceV2Base, params),
    deleteComplianceApplicationV2: ({ complianceId }) =>
      serviceV2.delete(SETTINGS_ENDPOINTS.complianceV2Detail(complianceId)),
    linkComplianceNumbers: (payload) =>
      serviceV2.post(SETTINGS_ENDPOINTS.complianceV2Link, payload),
    getIndiaCaf: () => serviceV2.get(SETTINGS_ENDPOINTS.complianceV2IndiaCaf),
    uploadIndiaCafUpdate: (formData) =>
      serviceV2.post(SETTINGS_ENDPOINTS.complianceV2IndiaCafUpdate, formData),
    getIndiaCafDocument: (documentId) =>
      serviceV2
        .get(SETTINGS_ENDPOINTS.complianceV2IndiaCafDocument(documentId), undefined, {
          responseType: 'blob',
        })
        .then(extractBlob),
    submitIndiaCafBusinessVerification: (formData) =>
      serviceV2.post(SETTINGS_ENDPOINTS.complianceV2IndiaCafBusinessVerification, formData),
    getIndiaCafBusinessVerificationForm: (verificationId) =>
      serviceV2
        .get(
          SETTINGS_ENDPOINTS.complianceV2IndiaCafBusinessVerificationForm(verificationId),
          undefined,
          { responseType: 'blob' },
        )
        .then(extractBlob),

    // Subaccounts
    listSubaccounts: (params) => defaultServiceV1.get(SETTINGS_ENDPOINTS.listSubaccounts, params),
    listSubaccountsInternal: (params) =>
      service.get(SETTINGS_ENDPOINTS.listSubaccountsInternal, params),
    getSubaccount: (subAuthId) => defaultServiceV1.get(SETTINGS_ENDPOINTS.getSubaccount(subAuthId)),
    createSubaccount: (payload) =>
      defaultServiceV1.post(SETTINGS_ENDPOINTS.createSubaccount, payload),
    updateSubaccount: (authId, payload) =>
      defaultServiceV1.post(SETTINGS_ENDPOINTS.updateSubaccount(authId), payload),
    deleteSubaccount: (authId, cascade) => {
      const params = cascade !== undefined ? { cascade } : undefined
      return defaultServiceV1.delete(SETTINGS_ENDPOINTS.deleteSubaccount(authId), undefined, {
        params,
      })
    },
    regenerateSubaccountAuthToken: (authId, payload) =>
      service.post(SETTINGS_ENDPOINTS.regenerateSubaccountAuthToken(authId), payload),
    deleteSubaccountAuthToken: (authId) =>
      service.delete(SETTINGS_ENDPOINTS.deleteSubaccountAuthToken(authId)),

    // Applications
    listApplications: (params) => defaultServiceV1.get(SETTINGS_ENDPOINTS.listApplications, params),
    getApplication: (appId) => defaultServiceV1.get(SETTINGS_ENDPOINTS.getApplication(appId)),

    // Internal Application API via Trace Service Proxy (routes to /v1/internal/Application/)
    listInternalApplications: (params) =>
      service.get(SETTINGS_ENDPOINTS.listInternalApplications, params),

    // Phone Numbers via Plivo API Proxy (routes to /v1/plivo-api/Number/)
    listNumbers: (params) => defaultServiceV1.get(SETTINGS_ENDPOINTS.listNumbers, params),
    // Lookup API via Plivo API Proxy (routes to /v1/plivo-api/Lookup/Number/{number}/)
    lookupNumber: (phoneNumber: string) =>
      defaultServiceV1.get(SETTINGS_ENDPOINTS.lookupNumber(phoneNumber), { type: 'carrier' }),
    // Plivo Verification via Hodor Proxy (routes to /v1/plivo-api/*)
    initiatePlivoVerification: (payload) =>
      defaultServiceV1.post(SETTINGS_ENDPOINTS.initiatePlivoVerification, payload),
    verifyPlivoCallerId: (verification_uuid, payload) =>
      defaultServiceV1.post(SETTINGS_ENDPOINTS.verifyPlivoCallerId(verification_uuid), payload),
    createApplication: (payload) =>
      defaultServiceV1.post(SETTINGS_ENDPOINTS.createApplication, payload),
    updateApplication: (appId, payload) =>
      defaultServiceV1.post(SETTINGS_ENDPOINTS.updateApplication(appId), payload),
    deleteApplication: (appId, params) =>
      defaultServiceV1.delete(SETTINGS_ENDPOINTS.deleteApplication(appId), undefined, {
        params,
      }),

    // SIP Trunks via Plivo API Proxy (routes to /v1/plivo-api/Zentrunk/Trunk/)
    listTrunks: (params) => defaultServiceV1.get(SETTINGS_ENDPOINTS.listTrunks, params),
    createTrunk: (payload) => defaultServiceV1.post(SETTINGS_ENDPOINTS.createTrunk, payload),
    getTrunk: (trunkId) => defaultServiceV1.get(SETTINGS_ENDPOINTS.getTrunk(trunkId)),
    updateTrunk: (trunkId, payload) =>
      defaultServiceV1.post(SETTINGS_ENDPOINTS.updateTrunk(trunkId), payload),
    deleteTrunk: (trunkId) => defaultServiceV1.delete(SETTINGS_ENDPOINTS.deleteTrunk(trunkId)),
    createIPAccessControlList: (payload) =>
      defaultServiceV1.post(SETTINGS_ENDPOINTS.ipAccessControlLists, payload),
    getIPAccessControlLists: (params) =>
      defaultServiceV1.get(SETTINGS_ENDPOINTS.ipAccessControlLists, params),
    getIPAccessControlList: (ipacl_uuid) =>
      defaultServiceV1.get(SETTINGS_ENDPOINTS.ipAccessControlList(ipacl_uuid)),
    updateIPAccessControlList: (ipacl_uuid, payload) =>
      defaultServiceV1.post(SETTINGS_ENDPOINTS.ipAccessControlList(ipacl_uuid), payload),
    deleteIPAccessControlList: (ipacl_uuid) =>
      defaultServiceV1.delete(SETTINGS_ENDPOINTS.ipAccessControlList(ipacl_uuid)),
    createCredential: (payload) => defaultServiceV1.post(SETTINGS_ENDPOINTS.credentials, payload),
    getCredentials: (params) => defaultServiceV1.get(SETTINGS_ENDPOINTS.credentials, params),
    getCredential: (credential_uuid) =>
      defaultServiceV1.get(SETTINGS_ENDPOINTS.credential(credential_uuid)),
    updateCredential: (credential_uuid, payload) =>
      defaultServiceV1.post(SETTINGS_ENDPOINTS.credential(credential_uuid), payload),
    deleteCredential: (credential_uuid) =>
      defaultServiceV1.delete(SETTINGS_ENDPOINTS.credential(credential_uuid)),
    createOriginationURI: (payload) =>
      defaultServiceV1.post(SETTINGS_ENDPOINTS.originationURIs, payload),
    getOriginationURIs: (params) =>
      defaultServiceV1.get(SETTINGS_ENDPOINTS.originationURIs, params),
    getOriginationURI: (uri_uuid) =>
      defaultServiceV1.get(SETTINGS_ENDPOINTS.originationURI(uri_uuid)),
    updateOriginationURI: (uri_uuid, payload) =>
      defaultServiceV1.post(SETTINGS_ENDPOINTS.originationURI(uri_uuid), payload),
    deleteOriginationURI: (uri_uuid) =>
      defaultServiceV1.delete(SETTINGS_ENDPOINTS.originationURI(uri_uuid)),
    // Voice Endpoints via Hodor Proxy (routes to /v1/plivo-api/Endpoint/*)
    listEndpoints: (params: Record<string, string>) =>
      defaultServiceV1.get(SETTINGS_ENDPOINTS.endpointBase, params),
    getEndpoint: (endpointId: string) =>
      defaultServiceV1.get(SETTINGS_ENDPOINTS.endpointDetails(endpointId)),
    createEndpoint: (payload: CreateEndpointPayload) =>
      defaultServiceV1.post(SETTINGS_ENDPOINTS.endpointBase, payload),
    updateEndpoint: (endpointId: string, payload: UpdateEndpointPayload) =>
      defaultServiceV1.post(SETTINGS_ENDPOINTS.endpointDetails(endpointId), payload),
    deleteEndpoint: (endpointId: string) =>
      defaultServiceV1.delete(SETTINGS_ENDPOINTS.endpointDetails(endpointId)),
    updateEndpointRouting: (endpointId: string, payload: UpdateEndpointRoutingPayload) =>
      serviceV2.patch(SETTINGS_ENDPOINTS.endpointRouting(endpointId), payload),

    // Rental Summary via Hodor Proxy (routes to /v1/contacto-core/v1/accountnumbers/rental-summary)
    getRentalSummary: (params: Record<string, string>) =>
      service.get(SETTINGS_ENDPOINTS.rentalSummary, params),

    // Unrented Numbers via Hodor Proxy (routes to /v1/contacto-core/v1/accountnumbers/unrented)
    getUnrentedNumbers: (params: Record<string, string>) =>
      service.get(SETTINGS_ENDPOINTS.unrentedNumbers, params),

    // Number Profile — account is resolved server-side from the AOM header, no params needed.
    getAllowedBuyCountries: () => service.get(SETTINGS_ENDPOINTS.allowedBuyCountries),

    // Zentrunk entitlement check (routes to /v1/zentrunk/account/enabled)
    getZentrunkAccountEnabled: () => service.get(SETTINGS_ENDPOINTS.zentrunkAccountEnabled),

    // SIP Inbound Trunks via Core API (routes to /v1/zentrunk/inbound/trunks)
    listInboundTrunksInternal: (params) =>
      service.get(SETTINGS_ENDPOINTS.listInboundTrunksInternal, params),
    getInboundTrunkById: (trunkId) => service.get(SETTINGS_ENDPOINTS.getInboundTrunkById(trunkId)),
    createInboundTrunkInternal: (payload) =>
      service.post(SETTINGS_ENDPOINTS.listInboundTrunksInternal, payload),
    updateInboundTrunkInternal: (trunkId, payload) =>
      service.put(SETTINGS_ENDPOINTS.getInboundTrunkById(trunkId), payload),
    getSipTrunkPlatforms: () => service.get(SETTINGS_ENDPOINTS.sipTrunkPlatforms),
    recordSipTrunkPlatform: (trunkRef, payload) =>
      service.put(SETTINGS_ENDPOINTS.sipTrunkPlatform(trunkRef), payload),
    forgetSipTrunkPlatform: (trunkRef) =>
      service.delete(SETTINGS_ENDPOINTS.sipTrunkPlatform(trunkRef)),
    getZentrunkURIs: (params) => service.get(SETTINGS_ENDPOINTS.getZentrunkURIs, params),
    createZentrunkURI: (payload) => service.post(SETTINGS_ENDPOINTS.createZentrunkURI, payload),
    updateZentrunkURI: (uriId, payload) =>
      service.put(SETTINGS_ENDPOINTS.updateZentrunkURI(uriId), payload),
    deleteZentrunkURI: (uriId) => service.delete(SETTINGS_ENDPOINTS.deleteZentrunkURI(uriId)),
    linkInboundTrunkNumber: (payload) =>
      service.post(SETTINGS_ENDPOINTS.linkInboundTrunkNumber, payload),
    unlinkInboundTrunkNumber: (phoneNumber) =>
      service.delete(SETTINGS_ENDPOINTS.unlinkInboundTrunkNumber(phoneNumber)),

    // Port-in Management
    listPortInOrders: (params: Record<string, string>) =>
      service.get(SETTINGS_ENDPOINTS.portInOrders, params),
    getPortInOrderDetails: (requestId: string) =>
      service.get(SETTINGS_ENDPOINTS.portInOrderDetails(requestId)),
    getPortInNumberConfigDetails: (requestId: string) =>
      service.get(SETTINGS_ENDPOINTS.portInNumberConfigDetails, { request_id: requestId }),
    exportPortInOrders: (params: Record<string, string>) => {
      const isSelected = params.export_type === 'selected'
      return service.get(SETTINGS_ENDPOINTS.exportPortInOrders, params, {
        responseType: isSelected ? 'blob' : 'json',
      })
    },
    createPortInOrder: (formData: FormData) =>
      service.post(SETTINGS_ENDPOINTS.createPortInOrder, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }),
    updatePortInOrder: (
      requestId: string,
      numbers: string,
      formData: FormData,
      updateMapForm: string,
    ) => {
      const params = new URLSearchParams({ numbers, port_in_request_id: requestId })
      formData.append('update_map_form', updateMapForm)
      return service.post(
        `${SETTINGS_ENDPOINTS.updatePortInOrder}?${params.toString()}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
    },
    getUSHolidays: () => service.get(SETTINGS_ENDPOINTS.getUSHolidays),
    updatePortInOrderConfig: (
      requestId: string,
      numberConfigInfo: Array<{
        number: string
        alias: string
        cnam?: string
        application?: string
        capabilities: string
      }>,
    ) =>
      service.post(SETTINGS_ENDPOINTS.updatePortInOrderConfig(requestId), {
        number_config_info: numberConfigInfo,
      }),
    validatePortInPhoneNumbers: (phoneNumbers: string[], requestId?: string) =>
      service.post(SETTINGS_ENDPOINTS.validatePortInPhoneNumbers, {
        phone_numbers: phoneNumbers,
        request_id: requestId,
      }),
    cancelPortInNumber: (requestId: string, phoneNumber: string) =>
      service.post(SETTINGS_ENDPOINTS.cancelPortInNumber(requestId), {
        phone_number: phoneNumber,
      }),
    changePortInDueDate: (requestId: string, phoneNumber: string, desiredDueDate: string) =>
      service.post(SETTINGS_ENDPOINTS.changePortInDueDate(requestId), {
        phone_number: phoneNumber,
        desired_due_date: desiredDueDate,
      }),
    downloadPortInDocument: (requestId: string, documentId: string) =>
      service
        .get(SETTINGS_ENDPOINTS.downloadPortInDocument(requestId, documentId), undefined, {
          responseType: 'blob',
        })
        .then(extractBlob),
    // SMS Delivery Alerts
    getSmsAlertSettings: () => service.get(SETTINGS_ENDPOINTS.smsAlertSettings),
    updateSmsAlertSettings: (payload) => service.post(SETTINGS_ENDPOINTS.smsAlertSettings, payload),

    // SMS Other Settings
    getSmsOtherSettings: () => service.get(SETTINGS_ENDPOINTS.smsOtherSettings),
    updateSmsOtherSettings: (payload) => service.post(SETTINGS_ENDPOINTS.smsOtherSettings, payload),

    // Voice Settings
    getVoiceSettings: (subaccountAuthId) =>
      service.get(
        subaccountAuthId
          ? `${SETTINGS_ENDPOINTS.voiceSettings}?subaccount_auth_id=${subaccountAuthId}`
          : SETTINGS_ENDPOINTS.voiceSettings,
      ),
    updateVoiceSettings: (payload) => service.post(SETTINGS_ENDPOINTS.voiceSettings, payload),

    // Zentrunk file capture consent (SIP Trunking Settings)
    getZentrunkFileCaptureConsent: () => service.get(SETTINGS_ENDPOINTS.zentrunkFileCaptureConsent),
    updateZentrunkFileCaptureConsent: (payload) =>
      service.post(SETTINGS_ENDPOINTS.zentrunkFileCaptureConsent, payload),

    getNumbersOtherSettings: () => service.get(SETTINGS_ENDPOINTS.numbersOtherSettings),
    updateNumbersOtherSettings: (payload) =>
      service.post(SETTINGS_ENDPOINTS.numbersOtherSettings, payload),

    updateNumberCnamLookup: (payload) => service.post(SETTINGS_ENDPOINTS.numberCnamLookup, payload),
    updateNumberCnam: (payload) => service.post(SETTINGS_ENDPOINTS.numberCnam, payload),
    bulkDeleteNumbers: (payload) =>
      service.post(SETTINGS_ENDPOINTS.accountNumbersBulkDelete, payload),

    // Onboarding
    completeOnboarding: (payload) => service.post(SETTINGS_ENDPOINTS.completeOnboarding, payload),

    // IP Whitelisting
    listIPWhitelist: (params) => service.get(SETTINGS_ENDPOINTS.ipWhitelist, params),
    addIPWhitelist: (payload) => service.post(SETTINGS_ENDPOINTS.ipWhitelist, payload),
    deleteIPWhitelist: (cidrId) => service.delete(SETTINGS_ENDPOINTS.deleteIpWhitelist(cidrId)),
  }
}

export const settingsService = serviceFactory()
